import { twMerge } from '@sortlist-frontend/design-system';
import { TFunction, useTranslation } from '@sortlist-frontend/translation/ssr';
import { useEffect, useState } from 'react';

import { HubSpotDemoLink } from '_components/HubSpotDemoLink';
import { usePublicAppContext } from '_core/context/public-app-context';
import { homeConfig } from '_features/home/home.config';

const ROTATOR_ITEMS = [
  'digitalMarketing',
  'branding',
  'webDevelopment',
  'seo',
  'design',
  'advertising',
  'ecommerce',
  'consulting',
] as const;

type RotatorItem = (typeof ROTATOR_ITEMS)[number];

const ROTATION_INTERVAL_MS = 1000;

const getRotatorLabel = (t: TFunction, type: RotatorItem): string => {
  switch (type) {
    case 'digitalMarketing':
      return t('home:sections.heroNew.rotator.digitalMarketing');
    case 'branding':
      return t('home:sections.heroNew.rotator.branding');
    case 'webDevelopment':
      return t('home:sections.heroNew.rotator.webDevelopment');
    case 'seo':
      return t('home:sections.heroNew.rotator.seo');
    case 'design':
      return t('home:sections.heroNew.rotator.design');
    case 'advertising':
      return t('home:sections.heroNew.rotator.advertising');
    case 'ecommerce':
      return t('home:sections.heroNew.rotator.ecommerce');
    case 'consulting':
      return t('home:sections.heroNew.rotator.consulting');
  }
};

export const HeroNew = () => {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const { getUrl } = usePublicAppContext();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % ROTATOR_ITEMS.length);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="pt-40 pb-24 bg-white text-center">
      <div className="container-lg px-6">
        <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-secondary-950 max-w-4xl mx-auto mb-6 leading-tight tracking-tight">
          {t('home:sections.heroNew.title')}
          <br />
          <span className="text-primary-600 transition-opacity duration-500">
            {getRotatorLabel(t, ROTATOR_ITEMS[activeIndex])}
          </span>
          <span className="text-primary-600 font-serif">.</span>
          <br />
          {t('home:sections.heroNew.everyDay')}
          <span className="text-primary-600 font-serif">.</span>
        </h1>

        <p className="text-lg text-secondary-600 max-w-2xl mx-auto mb-10 leading-relaxed">
          {t('home:sections.heroNew.subtitle')}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
          <HubSpotDemoLink
            className={twMerge(
              'inline-flex items-center bg-primary-600 text-white',
              'font-medium text-base px-6 py-3.5 rounded-lg',
              'hover:bg-primary-700 transition-colors',
              'shadow-md hover:shadow-lg',
              'leading-none',
            )}>
            {t('home:sections.heroNew.bookDemo')}
          </HubSpotDemoLink>
        </div>

        <p className="text-sm text-secondary-500">
          {t('home:sections.heroNew.lookingForAgency')}{' '}
          <a href={getUrl('project.new', {})} className="text-primary-500 font-medium hover:underline">
            {t('home:sections.heroNew.postProject')}
          </a>{' '}
          {t('home:sections.heroNew.or')}{' '}
          <a href={getUrl('client.explore')} className="text-primary-500 font-medium hover:underline">
            {t('home:sections.heroNew.browseAgencies')}
          </a>
        </p>
      </div>
    </section>
  );
};
